import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaChevronDown, FaGlobeAfrica, FaTimes } from "react-icons/fa";

const countries = [
  { code: "UG", name: "Uganda" },
  { code: "KE", name: "Kenya" },
  { code: "TZ", name: "Tanzania" },
  { code: "RW", name: "Rwanda" },
];

const navItems = [
  { label: "Home", path: "/" },
  { label: "How it works", section: "how-it-works" },
  { label: "Garages", section: "join" },
  { label: "About", section: "about" },
];

const getStoredCountry = () => {
  try {
    const stored = localStorage.getItem("country");
    if (!stored) return countries[0];

    const currentShape = countries.find(
      (country) => country.name === stored || country.code === stored
    );
    if (currentShape) return currentShape;

    const previousShape = JSON.parse(stored);
    const migrated = countries.find((country) => country.name === previousShape?.name);
    if (migrated) {
      return migrated;
    }

    return countries[0];
  } catch {
    return countries[0];
  }
};

const scrollToSection = (id) => {
  const target = document.getElementById(id);
  if (!target) return;

  const offset = 104;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
};

const Navbar = () => {
  const [selectedCountry, setSelectedCountry] = useState(getStoredCountry);
  const [scrolled, setScrolled] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    localStorage.setItem("country", selectedCountry.code);
  }, [selectedCountry]);

  const handleNavClick = (item) => {
    setMobileMenuOpen(false);
    setCountryOpen(false);

    if (item.path) {
      navigate(item.path);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (location.pathname === "/") {
      scrollToSection(item.section);
      return;
    }

    navigate("/");
    setTimeout(() => scrollToSection(item.section), 120);
  };

  return (
    <nav
      className={`fixed left-1/2 top-4 z-50 w-[calc(100%-32px)] max-w-6xl -translate-x-1/2 rounded-lg border px-4 py-3 transition-all duration-300 ${
        scrolled
          ? "border-[#D8E5D0] bg-[#F8FBF3]/95 text-[#121914] shadow-xl backdrop-blur"
          : "border-white/18 bg-[#F8FBF3]/90 text-[#121914] shadow-lg backdrop-blur"
      }`}
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex min-w-0 shrink-0 items-center gap-3"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Carmie home"
        >
          <img src="/carmielogo.png" alt="" className="h-9 w-auto shrink-0 sm:h-10" />
          <span className="hidden border-l border-[#121914]/15 pl-3 text-[11px] font-black uppercase tracking-[0.16em] text-[#49624B] xl:block">
            Driver service network
          </span>
        </Link>

        <ul className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <li key={item.label}>
              <button
                type="button"
                onClick={() => handleNavClick(item)}
                className="rounded-md px-3 py-2 text-sm font-bold text-[#253029] transition hover:bg-[#E6F6D8] hover:text-[#18360F] focus:outline-none focus:ring-2 focus:ring-[#8FE85E]"
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-2 lg:flex">
          <button
            type="button"
            onClick={() => handleNavClick({ section: "get-the-app" })}
            className="rounded-md bg-[#111714] px-4 py-2 text-sm font-black text-[#D6F7C6] transition hover:bg-[#1E2A22] focus:outline-none focus:ring-2 focus:ring-[#8FE85E]"
          >
            Get app
          </button>
          <div className="relative">
            <button
              type="button"
              className="flex items-center gap-2 rounded-md border border-[#D6E2CE] bg-white px-3 py-2 text-sm font-extrabold text-[#253029] transition hover:border-[#8FE85E] focus:outline-none focus:ring-2 focus:ring-[#8FE85E]"
              onClick={() => setCountryOpen((open) => !open)}
              aria-expanded={countryOpen}
              aria-label={`Current market: ${selectedCountry.name}`}
            >
              <FaGlobeAfrica aria-hidden="true" className="text-[#457A31]" />
              {selectedCountry.code}
              <FaChevronDown aria-hidden="true" className="text-[10px] text-[#6B786D]" />
            </button>

            {countryOpen && (
              <div className="absolute right-0 mt-2 w-40 rounded-md border border-[#D6E2CE] bg-white p-1 shadow-xl">
                {countries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    className="block w-full rounded px-3 py-2 text-left text-sm font-semibold text-[#253029] hover:bg-[#E6F6D8]"
                    onClick={() => {
                      setSelectedCountry(country);
                      setCountryOpen(false);
                    }}
                  >
                    {country.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-[#D6E2CE] bg-white text-xl text-[#121914] lg:hidden"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="mt-3 border-t border-[#D6E2CE] pt-3 lg:hidden">
          <ul className="grid gap-1">
            {[...navItems, { label: "Get the app", section: "get-the-app" }].map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  onClick={() => handleNavClick(item)}
                  className="block w-full rounded-md px-3 py-3 text-left text-base font-bold text-[#253029] hover:bg-[#E6F6D8]"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between rounded-md bg-[#111714] px-3 py-3 text-sm font-bold text-white">
            <span>Market</span>
            <span className="text-[#D6F7C6]">{selectedCountry.name}</span>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
